import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  useTheme,
  Skeleton,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Euro as EuroIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as FileDownloadIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  CompareArrows as CompareArrowsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import CountUp from "react-countup";
import { motion, AnimatePresence } from "framer-motion";
import { tokens } from "../theme";
import { usePreferences } from "../contexts/PreferencesContext";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
};

const cardHoverVariants = {
  rest: { scale: 1, y: 0 },
  hover: { scale: 1.02, y: -4, transition: { duration: 0.2 } },
};
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Line,
  ComposedChart,
} from "recharts";
import CATEGORY_COLORS from "../constants/categoryColors";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Calculate Irish business deadlines
const calculateIrishDeadlines = (yearEndMonth = 11) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const deadlines = [];

  // VAT Return deadlines (bi-monthly, due 23rd of following month)
  // Periods: Jan-Feb, Mar-Apr, May-Jun, Jul-Aug, Sep-Oct, Nov-Dec
  const vatPeriods = [
    { period: "Nov-Dec", dueMonth: 0, dueDay: 23, periodEnd: 11 },
    { period: "Jan-Feb", dueMonth: 2, dueDay: 23, periodEnd: 1 },
    { period: "Mar-Apr", dueMonth: 4, dueDay: 23, periodEnd: 3 },
    { period: "May-Jun", dueMonth: 6, dueDay: 23, periodEnd: 5 },
    { period: "Jul-Aug", dueMonth: 8, dueDay: 23, periodEnd: 7 },
    { period: "Sep-Oct", dueMonth: 10, dueDay: 23, periodEnd: 9 },
  ];

  vatPeriods.forEach((vp) => {
    let dueYear = currentYear;
    if (vp.period === "Nov-Dec") dueYear = currentYear + 1;

    const dueDate = new Date(dueYear, vp.dueMonth, vp.dueDay);
    if (dueDate >= now) {
      deadlines.push({
        id: `vat-${vp.period}`,
        title: `VAT Return (${vp.period})`,
        dueDate,
        type: "vat",
        description: "File VAT3 return on ROS",
        link: "/dashboard/vat",
        externalLink: "https://www.ros.ie",
      });
    }
  });

  // Preliminary Tax - October 31st (or Nov 15th for ROS)
  const prelimTaxDate = new Date(currentYear, 9, 31); // Oct 31
  if (prelimTaxDate >= now) {
    deadlines.push({
      id: "prelim-tax",
      title: "Preliminary Tax",
      dueDate: prelimTaxDate,
      type: "tax",
      description: "Pay preliminary corporation tax for current year",
      link: null,
      externalLink: "https://www.ros.ie",
    });
  }

  // Corporation Tax - 9 months after year end
  // Assuming Dec year-end by default
  const yearEndDate = new Date(currentYear - 1, yearEndMonth, 31);
  const ctDueDate = new Date(yearEndDate);
  ctDueDate.setMonth(ctDueDate.getMonth() + 9);
  ctDueDate.setDate(23); // Usually 23rd of the month

  if (ctDueDate >= now) {
    deadlines.push({
      id: "corp-tax",
      title: "Corporation Tax Return",
      dueDate: ctDueDate,
      type: "tax",
      description: `File CT1 for year ended ${yearEndDate.toLocaleDateString(
        "en-IE",
        { month: "short", year: "numeric" }
      )}`,
      link: null,
      externalLink: "https://www.ros.ie",
    });
  }

  // Annual Return (CRO) - typically within 56 days of ARD
  // We'll show a generic reminder
  const ardDate = new Date(currentYear, now.getMonth() + 2, 1);
  deadlines.push({
    id: "cro-annual",
    title: "CRO Annual Return",
    dueDate: ardDate,
    type: "cro",
    description: "Check your Annual Return Date on CRO",
    link: null,
    externalLink: "https://core.cro.ie",
  });

  // Sort by due date and return next 4
  return deadlines.sort((a, b) => a.dueDate - b.dueDate).slice(0, 4);
};

// Get urgency level based on days until deadline
const getDeadlineUrgency = (dueDate) => {
  const now = new Date();
  const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0)
    return { level: "overdue", color: "error", label: "Overdue!" };
  if (diffDays <= 7)
    return { level: "urgent", color: "error", label: `${diffDays} days` };
  if (diffDays <= 14)
    return { level: "soon", color: "warning", label: `${diffDays} days` };
  if (diffDays <= 30)
    return { level: "upcoming", color: "info", label: `${diffDays} days` };
  return { level: "ok", color: "success", label: `${diffDays} days` };
};

export default function DashboardHome({ user }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accountingBasis: savedAccountingBasis, setAccountingBasis: saveAccountingBasis, preferencesLoaded } = usePreferences();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [accountingBasis, setAccountingBasisLocal] = useState("accrual"); // 'accrual' or 'cash'
  const [showComparison, setShowComparison] = useState(false); // Side-by-side comparison
  const [loading, setLoading] = useState(true);

  // YTD Financial Data
  const [ytdData, setYtdData] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    totalPayroll: 0,
    vatCollected: 0,
    vatPaid: 0,
    outstandingInvoices: 0,
    outstandingCount: 0,
    paidInvoiceCount: 0,
    receiptCount: 0,
    payrollCount: 0,
  });

  // Month by Month breakdown
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);

  // Expense categories for the year
  const [expenseCategories, setExpenseCategories] = useState([]);

  // Previous year data for comparison
  const [prevYearData, setPrevYearData] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    grossProfit: 0,
  });

  // Accounts health indicators
  const [accountsHealth, setAccountsHealth] = useState({
    uncategorizedCount: 0,
    missingVatCount: 0,
    overdueInvoices: 0,
    recentActivity: true,
  });

  // Calculate upcoming deadlines (memoized)
  const upcomingDeadlines = React.useMemo(() => {
    return calculateIrishDeadlines(11); // Default Dec year-end
  }, []);

  // Drawer for month detail
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Expand/collapse sections in drawer
  const [expandedSections, setExpandedSections] = useState({
    invoices: true,
    expenses: true,
    payroll: true,
  });

  // Initialize accounting basis from saved preferences
  useEffect(() => {
    if (preferencesLoaded && savedAccountingBasis) {
      setAccountingBasisLocal(savedAccountingBasis);
    }
  }, [preferencesLoaded, savedAccountingBasis]);

  // Handler to update accounting basis and persist to Firestore
  const setAccountingBasis = async (basis) => {
    setAccountingBasisLocal(basis);
    saveAccountingBasis(basis); // Update context
    
    // Persist to Firestore
    if (user?.uid) {
      try {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, {
          "preferences.accountingBasis": basis
        });
      } catch (error) {
        console.error("Error saving accounting basis preference:", error);
      }
    }
  };


  useEffect(() => {
    if (!user?.uid) return;

    const fetchYearData = async () => {
      try {
        setLoading(true);

        // Initialize monthly buckets with detailed data
        const monthData = MONTHS.map((name, idx) => ({
          month: name,
          monthIndex: idx,
          revenue: 0,
          expenses: 0,
          payroll: 0,
          net: 0,
          cumulative: 0, // Running total
          invoices: [], // Detailed list
          expenseItems: [], // Detailed list
          payrollItems: [], // Detailed list
          categories: {}, // Category breakdown
        }));

        // Fetch all Invoices
        const invoicesQ = query(
          collection(db, "invoices"),
          where("userId", "==", user.uid)
        );
        const invoicesSnap = await getDocs(invoicesQ);

        let totalRevenue = 0;
        let vatCollected = 0;
        let outstandingInvoices = 0;
        let outstandingCount = 0;
        let paidInvoiceCount = 0;
        let alternativeRevenue = 0; // Track alternative method's revenue for comparison

        invoicesSnap.forEach((docSnap) => {
          const d = docSnap.data();
          const dateVal = d.invoiceDate?.toDate
            ? d.invoiceDate.toDate()
            : new Date(d.invoiceDate);
          const amount = parseFloat(d.totalAmount || 0);
          const vat = parseFloat(d.vatAmount || 0);

          // Only count for selected year
          if (dateVal.getFullYear() === selectedYear) {
            const monthIdx = dateVal.getMonth();
            const invoiceInfo = {
              id: docSnap.id,
              invoiceNumber: d.invoiceNumber || d.invoiceNo,
              clientName: d.clientName || d.client?.name || "Unknown",
              amount,
              status: d.status,
            };

            // Revenue Recognition Logic
            // Accrual: Recognize on invoiceDate
            // Cash: Recognize on markedAsPaidAt (if paid)

            let shouldRecognizeRevenue = false;
            let revenueDate = dateVal;

            // Calculate for current basis
            if (accountingBasis === "accrual") {
              // Always recognize based on invoice date if year matches
              if (dateVal.getFullYear() === selectedYear) {
                shouldRecognizeRevenue = true;
              }
            } else {
              // Cash Basis: Only recognize if paid AND paid date is in selected year
              if (d.status === "paid" && d.markedAsPaidAt) {
                const paidDate = d.markedAsPaidAt.toDate
                  ? d.markedAsPaidAt.toDate()
                  : new Date(d.markedAsPaidAt);
                if (paidDate.getFullYear() === selectedYear) {
                  shouldRecognizeRevenue = true;
                  revenueDate = paidDate; // Revenue belongs to this month
                }
              }
            }

            // Also calculate alternative method for comparison
            if (d.status === "paid") {
              if (accountingBasis === "accrual") {
                // We're on accrual, so alternative is cash
                // Cash: only count if paid in selected year
                if (d.markedAsPaidAt) {
                  const paidDate = d.markedAsPaidAt.toDate
                    ? d.markedAsPaidAt.toDate()
                    : new Date(d.markedAsPaidAt);
                  if (paidDate.getFullYear() === selectedYear) {
                    alternativeRevenue += amount;
                  }
                }
              } else {
                // We're on cash, so alternative is accrual
                // Accrual: count if invoice date is in selected year
                if (dateVal.getFullYear() === selectedYear) {
                  alternativeRevenue += amount;
                }
              }
            }

            if (d.status === "paid") {
               if (shouldRecognizeRevenue) {
                  totalRevenue += amount;
                  vatCollected += vat;
                  monthData[revenueDate.getMonth()].revenue += amount;
               }
               paidInvoiceCount++;
            } else {
               // Outstanding count always useful context, but not revenue
               if (accountingBasis === "accrual" && dateVal.getFullYear() === selectedYear) {
                  outstandingInvoices += amount;
                  outstandingCount++;
               }
            }
            
            // Still add invoice to the list for drill-down context if it originated this year
            if (dateVal.getFullYear() === selectedYear) {
               monthData[dateVal.getMonth()].invoices.push(invoiceInfo);
            }
          }
        });


        // Fetch all Receipts
        const receiptsQ = query(
          collection(db, "receipts"),
          where("uid", "==", user.uid)
        );
        const receiptsSnap = await getDocs(receiptsQ);

        let totalExpenses = 0;
        let vatPaid = 0;
        let receiptCount = 0;
        const categories = {};

        receiptsSnap.forEach((docSnap) => {
          const d = docSnap.data();

          // Get the date
          let dateVal;
          if (d.receiptYear !== undefined && d.receiptMonth !== undefined) {
            dateVal = new Date(d.receiptYear, d.receiptMonth, 15);
          } else if (d.receiptDate) {
            dateVal = new Date(d.receiptDate);
          } else if (d.date?.toDate) {
            dateVal = d.date.toDate();
          } else if (d.createdAt?.toDate) {
            dateVal = d.createdAt.toDate();
          } else {
            return;
          }

          // Skip income categories
          const category = d.category || "Uncategorized";
          const incomeKeywords = ["revenue", "income", "sales"];
          if (
            incomeKeywords.some((kw) => category.toLowerCase().includes(kw))
          ) {
            return;
          }

          // Only count for selected year
          if (dateVal.getFullYear() === selectedYear) {
            const amount = parseFloat(d.amount || d.total || 0);
            const vat = parseFloat(d.vatAmount || d.vat || 0);
            
            // Expense Logic: Always separate VAT from cost for Profit calculation
            // Net Cost = Total - VAT
            const netCost = amount - vat;

            totalExpenses += netCost;
            vatPaid += vat;
            receiptCount++;

            // Add to monthly bucket
            const monthIdx = dateVal.getMonth();
            monthData[monthIdx].expenses += netCost;

            // Store expense details for drill-down
            monthData[monthIdx].expenseItems.push({
              id: docSnap.id,
              fileName: d.fileName || d.vendor || "Expense",
              amount,
              vat,
              category,
              date: dateVal,
            });

            // Aggregate by category (yearly)
            if (!categories[category]) {
              categories[category] = 0;
            }
            categories[category] += amount;

            // Aggregate by category (monthly)
            if (!monthData[monthIdx].categories[category]) {
              monthData[monthIdx].categories[category] = 0;
            }
            monthData[monthIdx].categories[category] += netCost;
          }
        });

        // Fetch all Payroll
        const payrollQ = query(
          collection(db, "payroll"),
          where("uid", "==", user.uid)
        );
        const payrollSnap = await getDocs(payrollQ);

        let totalPayroll = 0;
        let payrollCount = 0;

        payrollSnap.forEach((docSnap) => {
          const d = docSnap.data();
          const dateVal = d.createdAt?.toDate
            ? d.createdAt.toDate()
            : new Date(d.createdAt);

          if (dateVal.getFullYear() === selectedYear) {
            const amount = parseFloat(d.amount || 0);
            totalPayroll += amount;
            payrollCount++;

            const monthIdx = dateVal.getMonth();
            monthData[monthIdx].payroll += amount;

            // Store payroll details for drill-down
            monthData[monthIdx].payrollItems.push({
              id: docSnap.id,
              name: d.name || "Employee",
              amount,
              date: dateVal,
            });
          }
        });

        // Calculate net for each month (revenue - expenses - payroll)
        let cumulativeTotal = 0;
        monthData.forEach((m) => {
          m.net = m.revenue - m.expenses - m.payroll;
          cumulativeTotal += m.net;
          m.cumulative = cumulativeTotal;
        });

        setYtdData({
          totalRevenue,
          totalExpenses,
          totalPayroll,
          vatCollected,
          vatPaid,
          outstandingInvoices,
          outstandingCount,
          paidInvoiceCount,
          receiptCount,
          payrollCount,
        });

        setMonthlyBreakdown(monthData);

        // Calculate comparison data
        const altOutgoings = totalExpenses + totalPayroll;
        setComparisonData({
          alternativeRevenue: alternativeRevenue,
          alternativeProfit: alternativeRevenue - altOutgoings,
        });

        // Sort categories by amount
        const sortedCategories = Object.entries(categories)
          .map(([name, amount]) => ({ name, amount }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 8);
        setExpenseCategories(sortedCategories);

        // Calculate health indicators
        const uncategorizedCount = Object.entries(categories).find(
          ([cat]) => cat === "Uncategorized" || cat === "Other"
        );
        setAccountsHealth({
          uncategorizedCount: uncategorizedCount ? 1 : 0,
          missingVatCount: 0, // Could be calculated from receipts with no VAT
          overdueInvoices: outstandingCount,
          recentActivity: receiptCount > 0 || paidInvoiceCount > 0,
        });
      } catch (error) {
        console.error("Error fetching year data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchYearData();
  }, [user, selectedYear, accountingBasis]);

  // Fetch previous year data for comparison
  useEffect(() => {
    if (!user?.uid) return;

    const fetchPrevYearData = async () => {
      try {
        const prevYear = selectedYear - 1;
        let prevRevenue = 0;
        let prevExpenses = 0;

        // Fetch invoices for previous year
        const invoicesQ = query(
          collection(db, "invoices"),
          where("userId", "==", user.uid)
        );
        const invoicesSnap = await getDocs(invoicesQ);

        invoicesSnap.forEach((docSnap) => {
          const d = docSnap.data();
          const dateVal = d.invoiceDate?.toDate
            ? d.invoiceDate.toDate()
            : new Date(d.invoiceDate);
          if (dateVal.getFullYear() === prevYear && d.status === "paid") {
            prevRevenue += parseFloat(d.totalAmount || 0);
          }
        });

        // Fetch receipts for previous year
        const receiptsQ = query(
          collection(db, "receipts"),
          where("uid", "==", user.uid)
        );
        const receiptsSnap = await getDocs(receiptsQ);

        receiptsSnap.forEach((docSnap) => {
          const d = docSnap.data();
          const dateVal = d.uploadedAt?.toDate?.() || d.receiptDate?.toDate?.();
          if (dateVal && dateVal.getFullYear() === prevYear) {
            prevExpenses += parseFloat(d.amount || d.total || 0);
          }
        });

        setPrevYearData({
          totalRevenue: prevRevenue,
          totalExpenses: prevExpenses,
          grossProfit: prevRevenue - prevExpenses,
        });
      } catch (error) {
        console.error("Error fetching prev year data:", error);
      }
    };

    fetchPrevYearData();
  }, [user, selectedYear]);

  // Calculate key metrics (now includes payroll)
  const totalOutgoings = ytdData.totalExpenses + ytdData.totalPayroll;
  const grossProfit = ytdData.totalRevenue - totalOutgoings;
  const profitMargin =
    ytdData.totalRevenue > 0 ? (grossProfit / ytdData.totalRevenue) * 100 : 0;
  const vatOwed = ytdData.vatCollected - ytdData.vatPaid;

  // State for alternative method comparison (populated during data fetch)
  const [comparisonData, setComparisonData] = useState({
    alternativeRevenue: 0,
    alternativeProfit: 0,
  });

  // Calculate year-over-year changes
  const yoyRevenueChange =
    prevYearData.totalRevenue > 0
      ? ((ytdData.totalRevenue - prevYearData.totalRevenue) /
          prevYearData.totalRevenue) *
        100
      : 0;
  const yoyProfitChange =
    prevYearData.grossProfit !== 0
      ? ((grossProfit - prevYearData.grossProfit) /
          Math.abs(prevYearData.grossProfit)) *
        100
      : 0;

  // Calculate quarterly data for Revenue VAT returns
  const quarterlyData = [
    { quarter: "Q1", months: [0, 1, 2], label: "Jan-Mar" },
    { quarter: "Q2", months: [3, 4, 5], label: "Apr-Jun" },
    { quarter: "Q3", months: [6, 7, 8], label: "Jul-Sep" },
    { quarter: "Q4", months: [9, 10, 11], label: "Oct-Dec" },
  ].map((q) => {
    const quarterMonths = monthlyBreakdown.filter((m, idx) =>
      q.months.includes(idx)
    );
    return {
      ...q,
      revenue: quarterMonths.reduce((sum, m) => sum + m.revenue, 0),
      expenses: quarterMonths.reduce((sum, m) => sum + m.expenses, 0),
      payroll: quarterMonths.reduce((sum, m) => sum + m.payroll, 0),
      net: quarterMonths.reduce((sum, m) => sum + m.net, 0),
    };
  });

  // Calculate accounts readiness score
  const accountsReadinessScore = (() => {
    let score = 100;
    if (accountsHealth.uncategorizedCount > 0) score -= 20;
    if (accountsHealth.overdueInvoices > 3) score -= 15;
    if (!accountsHealth.recentActivity) score -= 10;
    if (ytdData.receiptCount < ytdData.paidInvoiceCount / 2) score -= 15; // Few receipts relative to invoices
    return Math.max(0, score);
  })();

  // Handle month row click
  const handleMonthClick = (monthData) => {
    setSelectedMonth(monthData);
    setDrawerOpen(true);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Get current month index for highlighting
  const currentMonthIdx = new Date().getMonth();
  const isCurrentYear = selectedYear === currentYear;

  // Stat Card Component with Tooltip
  const StatCard = ({
    title,
    amount,
    subtitle,
    icon: Icon,
    color,
    isNegative,
    tooltip,
  }) => (
    <Paper
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        borderTop: `4px solid ${color}`,
      }}
    >
      <Stack spacing={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {title}
            </Typography>
            {tooltip && (
              <Tooltip title={tooltip} arrow placement="top">
                <InfoIcon
                  sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }}
                />
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(color, 0.1),
              color: color,
            }}
          >
            <Icon fontSize="small" />
          </Box>
        </Box>
        {loading ? (
          <Skeleton width="70%" height={45} />
        ) : (
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ letterSpacing: -1 }}
            color={isNegative && amount < 0 ? "error.main" : "text.primary"}
          >
            {amount < 0 ? "-" : ""}€
            <CountUp
              end={Math.abs(amount)}
              decimals={2}
              duration={1}
              separator=","
            />
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  // Format currency helper
  const formatMoney = (amount) => {
    return `€${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Custom tooltip for the chart
  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            {label} {selectedYear}
          </Typography>
          {payload.map((entry, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: entry.color,
                  }}
                />
                <Typography variant="body2">{entry.name}</Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {formatMoney(entry.value)}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Get the most urgent action needed
  const getUrgentAction = () => {
    if (upcomingDeadlines.length > 0) {
      const urgent = upcomingDeadlines.find(
        (d) => getDeadlineUrgency(d.dueDate).level === "urgent"
      );
      if (urgent) return { type: "deadline", item: urgent };
    }
    if (ytdData.outstandingCount > 0) {
      return {
        type: "invoices",
        count: ytdData.outstandingCount,
        amount: ytdData.outstandingInvoices,
      };
    }
    return null;
  };

  const urgentAction = getUrgentAction();

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box sx={{ maxWidth: 1200, mx: "auto", pb: 4 }}>
        {/* Clean Header */}
        <Box mb={4}>
          <motion.div variants={itemVariants}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
              mb={3}
            >
              <Box>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ letterSpacing: "-0.02em" }}
                >
                  {getGreeting()} 👋
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={0.5}>
                  Here's how your business is doing in {selectedYear}
                </Typography>
              </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip 
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700}>Accrual Basis</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Revenue recorded when <strong>invoice is issued</strong>, regardless of payment date.
                          </Typography>
                          <Typography variant="caption" color="warning.light" sx={{ display: "block", mt: 1 }}>
                            🇮🇪 Required for Irish companies registered with CRO
                          </Typography>
                        </Box>
                      } 
                      arrow 
                      placement="bottom"
                    >
                      <Button
                        size="small"
                        onClick={() => setAccountingBasis("accrual")}
                        sx={{
                          borderRadius: 0,
                          bgcolor: accountingBasis === "accrual" ? "primary.main" : "transparent",
                          color: accountingBasis === "accrual" ? "white" : "text.secondary",
                          "&:hover": { bgcolor: accountingBasis === "accrual" ? "primary.dark" : "action.hover" },
                          px: 2,
                        }}
                      >
                        Accrual
                      </Button>
                    </Tooltip>
                    <Tooltip 
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700}>Cash Basis</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Revenue recorded when <strong>payment is received</strong>, not when invoice is sent.
                          </Typography>
                          <Typography variant="caption" color="success.light" sx={{ display: "block", mt: 1 }}>
                            🇮🇪 Simpler option for sole traders and small businesses
                          </Typography>
                        </Box>
                      } 
                      arrow 
                      placement="bottom"
                    >
                      <Button
                        size="small"
                        onClick={() => setAccountingBasis("cash")}
                        sx={{
                          borderRadius: 0,
                          bgcolor: accountingBasis === "cash" ? "primary.main" : "transparent",
                          color: accountingBasis === "cash" ? "white" : "text.secondary",
                          "&:hover": { bgcolor: accountingBasis === "cash" ? "primary.dark" : "action.hover" },
                           px: 2,
                        }}
                      >
                        Cash Basis
                      </Button>
                    </Tooltip>
                  </Box>
                  <Tooltip title="Compare both methods side-by-side" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setShowComparison(!showComparison)}
                      sx={{
                        bgcolor: showComparison ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                        color: showComparison ? "primary.main" : "text.secondary",
                        border: "1px solid",
                        borderColor: showComparison ? "primary.main" : "divider",
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>


                 <FormControl size="small">
                   <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    sx={{
                      fontWeight: 600,
                      minWidth: 90,
                      borderRadius: 2,
                    }}
                  >
                    {[currentYear, currentYear - 1, currentYear - 2].map(
                      (year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DescriptionIcon />}
                  onClick={() => navigate("/dashboard/invoices/new")}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Invoice
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => navigate("/dashboard/upload")}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Expense
                </Button>
              </Stack>
            </Box>
          </motion.div>

          {/* Urgent Action Alert - Only show if something needs attention */}
          {urgentAction && (
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor:
                    urgentAction.type === "deadline"
                      ? alpha(theme.palette.error.main, 0.06)
                      : alpha(theme.palette.warning.main, 0.06),
                  border: `1px solid ${
                    urgentAction.type === "deadline"
                      ? alpha(theme.palette.error.main, 0.15)
                      : alpha(theme.palette.warning.main, 0.15)
                  }`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        urgentAction.type === "deadline"
                          ? alpha(theme.palette.error.main, 0.1)
                          : alpha(theme.palette.warning.main, 0.1),
                    }}
                  >
                    {urgentAction.type === "deadline" ? (
                      <ScheduleIcon color="error" />
                    ) : (
                      <WarningIcon color="warning" />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {urgentAction.type === "deadline"
                        ? `${urgentAction.item.title} due soon`
                        : `${urgentAction.count} unpaid invoice${
                            urgentAction.count > 1 ? "s" : ""
                          }`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {urgentAction.type === "deadline"
                        ? `Due ${urgentAction.item.dueDate.toLocaleDateString(
                            "en-IE",
                            { weekday: "short", day: "numeric", month: "short" }
                          )}`
                        : `${formatMoney(urgentAction.amount)} outstanding`}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  color={urgentAction.type === "deadline" ? "error" : "warning"}
                  onClick={() =>
                    navigate(
                      urgentAction.type === "deadline"
                        ? "/dashboard/vat"
                        : "/dashboard/invoices"
                    )
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {urgentAction.type === "deadline"
                    ? "Prepare Now"
                    : "View Invoices"}
                </Button>
              </Box>
            </motion.div>
          )}
        </Box>

        {/* Accounting Basis Comparison Card */}
        <AnimatePresence>
          {showComparison && (
            <motion.div
              initial={{ opacity: 0, height: 0, mb: 0 }}
              animate={{ opacity: 1, height: "auto", mb: 32 }}
              exit={{ opacity: 0, height: 0, mb: 0 }}
              style={{ overflow: "hidden" }}
            >
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CompareArrowsIcon color="info" />
                    <Typography variant="h6" fontWeight={700}>
                      Method Comparison: {accountingBasis === "accrual" ? "Accrual" : "Cash"} vs {accountingBasis === "accrual" ? "Cash" : "Accrual"}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setShowComparison(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                <Grid container spacing={4}>
                  {/* Current Method */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "primary.main",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bgcolor: "primary.main",
                          color: "white",
                          px: 1.5,
                          py: 0.5,
                          borderBottomLeftRadius: 8,
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        CURRENT
                      </Box>
                      <Typography variant="overline" color="text.secondary" fontWeight={600}>
                        {accountingBasis === "accrual" ? "Accrual Basis" : "Cash Basis"}
                      </Typography>
                      <Grid container spacing={2} mt={1}>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Revenue</Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {formatMoney(ytdData.totalRevenue)}
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Net Profit</Typography>
                          <Typography variant="h6" fontWeight={700} color={grossProfit >= 0 ? "success.main" : "error.main"}>
                            {formatMoney(grossProfit)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Alternative Method */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        border: "1px dashed",
                        borderColor: "text.disabled",
                      }}
                    >
                      <Typography variant="overline" color="text.secondary" fontWeight={600}>
                        IF YOU SWITCHED TO {accountingBasis === "accrual" ? "CASH BASIS" : "ACCRUAL BASIS"}
                      </Typography>
                      <Grid container spacing={2} mt={1}>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Revenue</Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {formatMoney(comparisonData.alternativeRevenue)}
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Net Profit</Typography>
                          <Typography variant="h6" fontWeight={700} color={comparisonData.alternativeProfit >= 0 ? "success.main" : "error.main"}>
                            {formatMoney(comparisonData.alternativeProfit)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hero Metrics - Single Focus */}
        <Grid container spacing={3} mb={4}>
          {/* Main Profit Card */}
          <Grid size={{ xs: 12, md: 5 }}>
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  height: "100%",
                  background:
                    grossProfit >= 0
                      ? `linear-gradient(135deg, ${alpha(
                          theme.palette.success.main,
                          0.04
                        )} 0%, ${alpha(theme.palette.success.main, 0.01)} 100%)`
                      : `linear-gradient(135deg, ${alpha(
                          theme.palette.error.main,
                          0.04
                        )} 0%, ${alpha(theme.palette.error.main, 0.01)} 100%)`,
                  border: `1px solid ${
                    grossProfit >= 0
                      ? alpha(theme.palette.success.main, 0.1)
                      : alpha(theme.palette.error.main, 0.1)
                  }`,
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Year-to-Date Profit
                </Typography>
                {loading ? (
                  <Skeleton width="60%" height={60} />
                ) : (
                  <Typography
                    variant="h2"
                    fontWeight={800}
                    sx={{
                      letterSpacing: -2,
                      color: grossProfit >= 0 ? "success.main" : "error.main",
                      my: 1,
                    }}
                  >
                    {grossProfit < 0 ? "-" : ""}€
                    <CountUp
                      end={Math.abs(grossProfit)}
                      decimals={0}
                      duration={1.5}
                      separator=","
                    />
                  </Typography>
                )}
                <Stack direction="row" spacing={3} mt={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Revenue
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="success.main"
                    >
                      +{formatMoney(ytdData.totalRevenue)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Expenses
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="error.main"
                    >
                      -{formatMoney(ytdData.totalExpenses)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payroll
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="warning.main"
                    >
                      -{formatMoney(ytdData.totalPayroll)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Margin
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {profitMargin.toFixed(0)}%
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>

          {/* Quick Stats Grid */}
          <Grid size={{ xs: 12, md: 7 }}>
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Grid container spacing={2} sx={{ height: "100%" }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <TrendingUpIcon
                      sx={{ color: "success.main", fontSize: 28, mb: 0.5 }}
                    />
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? (
                        <Skeleton width={60} sx={{ mx: "auto" }} />
                      ) : (
                        ytdData.paidInvoiceCount
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Paid Invoices
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      height: "100%",
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": { boxShadow: 2 },
                    }}
                    onClick={() => navigate("/dashboard/invoices")}
                  >
                    <WarningIcon
                      sx={{ color: "warning.main", fontSize: 28, mb: 0.5 }}
                    />
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? (
                        <Skeleton width={60} sx={{ mx: "auto" }} />
                      ) : (
                        ytdData.outstandingCount
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unpaid
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <ReceiptIcon
                      sx={{ color: "info.main", fontSize: 28, mb: 0.5 }}
                    />
                    <Typography variant="h5" fontWeight={700}>
                      {loading ? (
                        <Skeleton width={60} sx={{ mx: "auto" }} />
                      ) : (
                        ytdData.receiptCount
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receipts
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      height: "100%",
                      textAlign: "center",
                      bgcolor:
                        vatOwed >= 0
                          ? alpha(theme.palette.warning.main, 0.05)
                          : alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <EuroIcon
                      sx={{
                        color: vatOwed >= 0 ? "warning.main" : "success.main",
                        fontSize: 28,
                        mb: 0.5,
                      }}
                    />
                    <Typography variant="h6" fontWeight={700}>
                      {loading ? (
                        <Skeleton width={60} sx={{ mx: "auto" }} />
                      ) : (
                        formatMoney(Math.abs(vatOwed))
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      VAT {vatOwed >= 0 ? "Due" : "Refund"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </motion.div>
          </Grid>
        </Grid>

        {/* Compact Deadlines Strip */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              mb: 4,
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "divider",
                borderRadius: 3,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{ minWidth: "max-content", pb: 1 }}
            >
              {upcomingDeadlines.slice(0, 4).map((deadline) => {
                const urgency = getDeadlineUrgency(deadline.dueDate);
                return (
                  <Paper
                    key={deadline.id}
                    sx={{
                      p: 2,
                      minWidth: 220,
                      borderRadius: 3,
                      borderLeft: `3px solid ${
                        theme.palette[urgency.color].main
                      }`,
                      cursor: deadline.link ? "pointer" : "default",
                      transition: "all 0.2s",
                      "&:hover": deadline.link
                        ? { boxShadow: 2, transform: "translateY(-2px)" }
                        : {},
                    }}
                    onClick={() => deadline.link && navigate(deadline.link)}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 0.5 }}
                        >
                          {deadline.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {deadline.dueDate.toLocaleDateString("en-IE", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </Typography>
                      </Box>
                      <Chip
                        label={urgency.label}
                        size="small"
                        color={urgency.color}
                        sx={{ fontSize: 10, height: 20 }}
                      />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        </motion.div>

        {/* Main Content Grid */}
        <Grid container spacing={3} mb={4}>
          {/* Quarterly Performance - Clean Cards */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Quarterly Performance
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => {
                      const data =
                        `Tax Summary ${selectedYear}\n\n` +
                        `Revenue,€${ytdData.totalRevenue.toFixed(2)}\n` +
                        `Expenses,€${ytdData.totalExpenses.toFixed(2)}\n` +
                        `Profit,€${grossProfit.toFixed(2)}\n` +
                        `VAT Owed,€${vatOwed.toFixed(2)}\n`;
                      const blob = new Blob([data], { type: "text/csv" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `summary-${selectedYear}.csv`;
                      a.click();
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Export
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {quarterlyData.map((q) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={q.quarter}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.04),
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="overline" color="text.secondary">
                          {q.quarter}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          color={q.net >= 0 ? "success.main" : "error.main"}
                          sx={{ my: 1 }}
                        >
                          {q.net >= 0 ? "+" : ""}
                          {formatMoney(q.net)}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Typography variant="caption" color="success.main">
                            ↑{formatMoney(q.revenue)}
                          </Typography>
                          <Typography variant="caption" color="error.main">
                            ↓{formatMoney(q.expenses + q.payroll)}
                          </Typography>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Year-over-Year comparison */}
                {prevYearData.totalRevenue > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(
                        yoyRevenueChange >= 0
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        0.05
                      ),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {yoyRevenueChange >= 0 ? (
                        <TrendingUpIcon color="success" />
                      ) : (
                        <TrendingDownIcon color="error" />
                      )}
                      <Typography variant="body2">
                        Revenue vs {selectedYear - 1}
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color={
                        yoyRevenueChange >= 0 ? "success.main" : "error.main"
                      }
                    >
                      {yoyRevenueChange >= 0 ? "+" : ""}
                      {yoyRevenueChange.toFixed(0)}%
                    </Typography>
                  </Box>
                )}
              </Paper>
            </motion.div>
          </Grid>

          {/* Quick Actions Card */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Quick Actions
                </Typography>

                <Stack spacing={1.5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate("/dashboard/vat")}
                    startIcon={<AssessmentIcon />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "left", ml: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Prepare VAT Return
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMoney(Math.abs(vatOwed))}{" "}
                        {vatOwed >= 0 ? "due" : "refund"}
                      </Typography>
                    </Box>
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate("/dashboard/invoices")}
                    startIcon={<DescriptionIcon />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "left", ml: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Review Invoices
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ytdData.outstandingCount} unpaid
                      </Typography>
                    </Box>
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate("/dashboard/inbox")}
                    startIcon={<ReceiptIcon />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "left", ml: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Review Expenses
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ytdData.receiptCount} receipts logged
                      </Typography>
                    </Box>
                  </Button>
                </Stack>

                {/* Simple Health Indicator */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(
                      accountsReadinessScore >= 80
                        ? theme.palette.success.main
                        : accountsReadinessScore >= 50
                        ? theme.palette.warning.main
                        : theme.palette.error.main,
                      0.08
                    ),
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Accounts Readiness
                    </Typography>
                    <Chip
                      label={`${accountsReadinessScore}%`}
                      size="small"
                      color={
                        accountsReadinessScore >= 80
                          ? "success"
                          : accountsReadinessScore >= 50
                          ? "warning"
                          : "error"
                      }
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* Visual Chart */}
        <motion.div variants={itemVariants}>
          <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight={700} mb={3}>
              Monthly Trend
            </Typography>

            {loading ? (
              <Skeleton
                variant="rectangular"
                height={300}
                sx={{ borderRadius: 2 }}
              />
            ) : (
              <Box sx={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={monthlyBreakdown}
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorRevenue"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.success.main}
                          stopOpacity={0.9}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.success.main}
                          stopOpacity={0.5}
                        />
                      </linearGradient>
                      <linearGradient
                        id="colorExpenses"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.error.main}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.error.main}
                          stopOpacity={0.4}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={theme.palette.divider}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                      tickFormatter={(val) =>
                        `€${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`
                      }
                    />
                    <RechartsTooltip content={<CustomChartTooltip />} />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{ paddingTop: 15 }}
                      formatter={(value) => (
                        <span
                          style={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: 12,
                          }}
                        >
                          {value}
                        </span>
                      )}
                    />
                    <Bar
                      dataKey="revenue"
                      name="Money In"
                      fill="url(#colorRevenue)"
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                    <Bar
                      dataKey="expenses"
                      name="Money Out"
                      fill="url(#colorExpenses)"
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke={theme.palette.success.main}
                      strokeWidth={3}
                      dot={{ r: 4, strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="expenses"
                      name="Expenses"
                      stroke={theme.palette.error.main}
                      strokeWidth={3}
                      dot={{ r: 4, strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="payroll"
                      name="Payroll"
                      stroke={theme.palette.warning.main}
                      strokeWidth={3}
                      dot={{ r: 4, strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="net"
                      name="Net Profit"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.primary.main, r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </motion.div>

        {/* Month by Month Breakdown & Categories */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Paper
                sx={{ borderRadius: 3, overflow: "hidden", height: "100%" }}
              >
                <Box p={3} pb={2}>
                  <Typography variant="h6" fontWeight={700}>
                    Monthly Breakdown
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: "success.main" }}
                        >
                          Income
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: "error.main" }}
                        >
                          Expenses
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 600, color: "warning.main" }}
                        >
                          Payroll
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Net
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading
                        ? [1, 2, 3, 4].map((i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Skeleton width={40} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={60} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={60} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={60} />
                              </TableCell>
                            </TableRow>
                          ))
                        : monthlyBreakdown.map((row, idx) => {
                            const isCurrentMonth =
                              isCurrentYear && idx === currentMonthIdx;
                            const hasData = row.revenue > 0 || row.expenses > 0;
                            const showData =
                              hasData ||
                              idx <= currentMonthIdx ||
                              !isCurrentYear;

                            return (
                              <TableRow
                                key={row.month}
                                onClick={() => hasData && handleMonthClick(row)}
                                sx={{
                                  cursor: hasData ? "pointer" : "default",
                                  bgcolor: isCurrentMonth
                                    ? alpha(theme.palette.primary.main, 0.06)
                                    : "transparent",
                                  "&:hover": {
                                    bgcolor: hasData
                                      ? alpha(theme.palette.grey[500], 0.05)
                                      : "transparent",
                                  },
                                }}
                              >
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight={isCurrentMonth ? 700 : 500}
                                  >
                                    {row.month}
                                    {isCurrentMonth && (
                                      <Chip
                                        label="•"
                                        size="small"
                                        color="primary"
                                        sx={{ ml: 1, height: 16, minWidth: 16 }}
                                      />
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color={
                                      row.revenue > 0
                                        ? "success.main"
                                        : "text.disabled"
                                    }
                                  >
                                    {showData ? formatMoney(row.revenue) : "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color={
                                      row.expenses > 0
                                        ? "error.main"
                                        : "text.disabled"
                                    }
                                  >
                                    {showData ? formatMoney(row.expenses) : "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    color={
                                      row.payroll > 0
                                        ? "warning.main"
                                        : "text.disabled"
                                    }
                                  >
                                    {showData
                                      ? `-${formatMoney(row.payroll)}`
                                      : "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    color={
                                      row.net >= 0
                                        ? "success.main"
                                        : "error.main"
                                    }
                                  >
                                    {showData
                                      ? `${
                                          row.net >= 0 ? "+" : ""
                                        }${formatMoney(row.net)}`
                                      : "—"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      {/* Totals Row */}
                      <TableRow
                        sx={{ bgcolor: alpha(theme.palette.grey[500], 0.06) }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            Total
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="success.main"
                          >
                            {formatMoney(ytdData.totalRevenue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="error.main"
                          >
                            {formatMoney(ytdData.totalExpenses)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="warning.main"
                          >
                            {formatMoney(ytdData.totalPayroll)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={
                              grossProfit >= 0 ? "success.main" : "error.main"
                            }
                          >
                            {grossProfit >= 0 ? "+" : ""}
                            {formatMoney(grossProfit)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </motion.div>
          </Grid>

          {/* Expense Categories */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Expense Categories
                </Typography>

                {loading ? (
                  <Stack spacing={1.5}>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} height={32} />
                    ))}
                  </Stack>
                ) : expenseCategories.length > 0 ? (
                  <Stack spacing={1}>
                    {expenseCategories.slice(0, 6).map((cat) => {
                      const percentage =
                        ytdData.totalExpenses > 0
                          ? (cat.amount / ytdData.totalExpenses) * 100
                          : 0;
                      return (
                        <Box
                          key={cat.name}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          py={0.5}
                        >
                          <Typography variant="body2">{cat.name}</Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {formatMoney(cat.amount)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 40, textAlign: "right" }}
                            >
                              {percentage.toFixed(0)}%
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box py={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      No expenses recorded
                    </Typography>
                  </Box>
                )}
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* Month Detail Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { width: { xs: "100%", sm: 420 }, p: 0 },
          }}
        >
          {selectedMonth && (
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" fontWeight={700}>
                    {selectedMonth.month} {selectedYear}
                  </Typography>
                  <IconButton onClick={() => setDrawerOpen(false)} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Stack direction="row" spacing={2} mt={1}>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={600}
                  >
                    In: {formatMoney(selectedMonth.revenue)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error.main"
                    fontWeight={600}
                  >
                    Out: {formatMoney(selectedMonth.expenses)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={
                      selectedMonth.net >= 0 ? "success.main" : "error.main"
                    }
                  >
                    Net: {selectedMonth.net >= 0 ? "+" : ""}
                    {formatMoney(selectedMonth.net)}
                  </Typography>
                </Stack>
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                {/* Invoices Section */}
                <Paper sx={{ mb: 2, overflow: "hidden", borderRadius: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    onClick={() => toggleSection("invoices")}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionIcon color="success" />
                      <Typography fontWeight={600}>
                        Invoices ({selectedMonth.invoices?.length || 0})
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={700} color="success.main">
                        {formatMoney(selectedMonth.revenue)}
                      </Typography>
                      {expandedSections.invoices ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </Box>
                  </Box>
                  <Collapse in={expandedSections.invoices}>
                    <Divider />
                    {selectedMonth.invoices?.length > 0 ? (
                      <List dense sx={{ py: 0 }}>
                        {selectedMonth.invoices.map((inv, idx) => (
                          <ListItem
                            key={inv.id || idx}
                            sx={{
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <ListItemText
                              primary={inv.clientName}
                              secondary={`#${
                                inv.invoiceNumber
                              } • ${inv.date?.toLocaleDateString()}`}
                            />
                            <Box textAlign="right">
                              <Typography fontWeight={600} color="success.main">
                                {formatMoney(inv.amount)}
                              </Typography>
                              <Chip
                                label={inv.status}
                                size="small"
                                color={
                                  inv.status === "paid" ? "success" : "warning"
                                }
                                sx={{ height: 20, fontSize: 10 }}
                              />
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box p={2} textAlign="center">
                        <Typography color="text.secondary" variant="body2">
                          No invoices this month
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </Paper>

                {/* Expenses Section */}
                <Paper sx={{ mb: 2, overflow: "hidden", borderRadius: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    onClick={() => toggleSection("expenses")}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <ReceiptIcon color="error" />
                      <Typography fontWeight={600}>
                        Expenses ({selectedMonth.expenseItems?.length || 0})
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={700} color="error.main">
                        {formatMoney(selectedMonth.expenses)}
                      </Typography>
                      {expandedSections.expenses ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </Box>
                  </Box>
                  <Collapse in={expandedSections.expenses}>
                    <Divider />
                    {/* Category Breakdown */}
                    {Object.keys(selectedMonth.categories || {}).length > 0 && (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.05),
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="text.secondary"
                        >
                          BY CATEGORY
                        </Typography>
                        <Stack spacing={1} mt={1}>
                          {Object.entries(selectedMonth.categories)
                            .sort((a, b) => b[1] - a[1])
                            .map(([cat, amount]) => (
                              <Box
                                key={cat}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      bgcolor:
                                        CATEGORY_COLORS[cat] ||
                                        theme.palette.grey[400],
                                    }}
                                  />
                                  <Typography variant="body2">{cat}</Typography>
                                </Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {formatMoney(amount)}
                                </Typography>
                              </Box>
                            ))}
                        </Stack>
                      </Box>
                    )}
                    <Divider />
                    {selectedMonth.expenseItems?.length > 0 ? (
                      <List
                        dense
                        sx={{ py: 0, maxHeight: 300, overflow: "auto" }}
                      >
                        {selectedMonth.expenseItems.map((exp, idx) => (
                          <ListItem
                            key={exp.id || idx}
                            sx={{
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor:
                                    CATEGORY_COLORS[exp.category] ||
                                    theme.palette.grey[400],
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={exp.fileName}
                              secondary={`${
                                exp.category
                              } • ${exp.date?.toLocaleDateString()}`}
                            />
                            <Typography fontWeight={600} color="error.main">
                              {formatMoney(exp.amount)}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box p={2} textAlign="center">
                        <Typography color="text.secondary" variant="body2">
                          No expenses this month
                        </Typography>
                      </Box>
                    )}
                  </Collapse>
                </Paper>
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>
    </motion.div>
  );
}
